import { useCallback, useEffect, useMemo, useState } from 'react';

import { isAfter } from 'date-fns';
import { mapValues } from 'lodash-es';

import { IOffer } from '@rbi-ctg/menu';
import { IOfferRedemptions } from '@rbi-ctg/offers';
import { MenuObjectTypes } from 'enums/menu';
import {
  IEvaluateUserOffersQuery,
  IOfferFeedbackEntryFragment,
  useEvaluateUserOffersQuery
} from 'generated/rbi-graphql';
import { UserDetails } from 'state/auth/hooks/use-current-user';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { ServiceMode } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { filterMap } from 'utils/array';
import { platform } from 'utils/environment';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import logger from 'utils/logger';
import {
  RuleType,
  evaluateRestaurantGroupRestriction,
  getRequiredPlatforms,
  getRequiredServiceModes,
  isRedeemableWithLimit,
  offersFeedbacksById,
  redeemedOnTime,
  reduceOffersFeedback,
  requiresAssignmentWillApply
} from 'utils/offers';
import { getExpirationDateFromRuleset } from 'utils/offers/rule-sets/get-expiration-date';
import { parseSanityObject } from 'utils/sanity';

import { UIPattern } from '../types';

import { LOCALE_BLOCK_KEYS, templateKeyValuePairToObject } from './use-locked-offers';
import { couponIdsToQueryParams, makeRemainingEvaluateUserOfferQueries } from './utils';

export interface IUseOfferListProps {
  serviceMode: null | ServiceMode;
  sortedOffers: IOffer[];
  user: null | UserDetails;
}

const getAllRedeemedOffersNoAuth = (): IOfferRedemptions =>
  LocalStorage.getItem(StorageKeys.OFFERS_REDEEMED_NO_AUTH) || {};

// @todo if we have stored offers and a user signs up afterward,
// should we attribute those redemptions to that user, or allow
// them to redeem the offers again?
const persistUnauthenticatedRedemptions = (offerRedemptions: IOfferRedemptions) => {
  const allRedeemed = getAllRedeemedOffersNoAuth();
  LocalStorage.setItem(StorageKeys.OFFERS_REDEEMED_NO_AUTH, {
    ...allRedeemed,
    ...offerRedemptions
  });
  return allRedeemed;
};

const getCouponIdsFromNonActivationOffers = filterMap<IOffer, string>(
  ({ option }) => {
    const optionType = option && option._type;

    return optionType !== MenuObjectTypes.OFFER_ACTIVATION;
  },
  ({ _id }) => _id
);

/**
 * returns a list of currently usable offers for the given user
 * (if no user is logged in returns all offers)
 */

function useOffersList({ serviceMode, sortedOffers = [], user }: IUseOfferListProps) {
  const enableQueryOffersFromBackend = useFlag(LaunchDarklyFlag.ENABLE_QUERY_OFFERS_FROM_BACKEND);
  const { language } = useLocale();
  const preloadedOfferRedemptions = useMemo(getAllRedeemedOffersNoAuth, []);
  const [offers, setOffers] = useState(sortedOffers);
  const [standardOffers, setStandardOffers] = useState<IOffer[]>([]);
  const { store } = useStoreContext();
  const [redeemedOn, setRedeemedOn] = useState<string>(redeemedOnTime());
  const [offersFeedback, setOffersFeedback] = useState<
    Record<string, IOfferFeedbackEntryFragment> | undefined
  >();
  const [unauthenticatedRedemptions, setUnauthenticatedRedemptions] = useState(
    preloadedOfferRedemptions
  );

  /**
   * using Apollo's built-in notifyOnNetworkStatusChange parameter + networkStatus value did not work
   * for tracking the loading state of the query while fetchMore is used.
   * This variable is used instead
   */
  const [isLoadingOrFetchingMore, setIsLoadingOrFetchingMore] = useState(false);

  const cognitoId = user?.cognitoId ?? null;

  const storeId =
    (store.hasSelection ? store.number : store.selectedUnavailableStoreNumber) ?? null;

  // memoize to avoid using a new `redeemedOn` value every render,
  // which would trigger a re-fetch
  const [firstQueryParams, ...restOfQueryParamsList] = useMemo(() => {
    const couponIds = getCouponIdsFromNonActivationOffers(sortedOffers);
    // if enableQueryOffersFromBackend is true, we set couponIds to empty and evaluateAllCouponIds to true.
    return couponIdsToQueryParams({
      couponIds: enableQueryOffersFromBackend ? [] : couponIds,
      evaluateAllCouponIds: enableQueryOffersFromBackend,
      platform: platform(),
      serviceMode,
      redeemedOn,
      storeId
    });
  }, [enableQueryOffersFromBackend, redeemedOn, serviceMode, sortedOffers, storeId]);

  // make the first EvaluateUserOffers query, and then trigger the rest in the onCompleted callback
  const { data, loading, fetchMore, refetch } = useEvaluateUserOffersQuery({
    context: { shouldNotBatch: true },
    fetchPolicy: 'network-only',
    skip: enableQueryOffersFromBackend ? false : !sortedOffers.length || !user,
    variables: firstQueryParams,
    partialRefetch: true,
    onCompleted: async () => {
      try {
        await makeRemainingEvaluateUserOfferQueries(fetchMore, restOfQueryParamsList, !!user);
      } catch (error) {
        logger.error(`error during fetchMore on evaluateUserOffers ${error.message}`);
      }
      setIsLoadingOrFetchingMore(false);
    }
  });

  const queryRedeemableOffers = useCallback<() => void>(() => {
    if (cognitoId || enableQueryOffersFromBackend) {
      setRedeemedOn(redeemedOnTime());
    }
  }, [cognitoId, enableQueryOffersFromBackend]);

  const redeemUnauthenticatedOffer = useCallback((couponId: string) => {
    setUnauthenticatedRedemptions(redemptions => {
      const redemptionDateTime = redeemedOnTime();
      if (redemptions[couponId] && Array.isArray(redemptions[couponId])) {
        return {
          ...redemptions,
          [couponId]: redemptions[couponId].concat(redemptionDateTime)
        };
      }

      return {
        ...redemptions,
        [couponId]: [redemptionDateTime]
      };
    });
  }, []);

  // we want to avoid displaying offers the user cannot redeem
  // some of this feedback comes from the evaluateUserOffers query
  // and some has to be determined by analyzing the offer's rules
  // and checking if the user has redeemed the offer as an
  // "unauthenticated redemption"
  const shouldDisplayOffer = useCallback(
    (offer: IOffer): boolean => {
      const requiredPlatforms = getRequiredPlatforms(offer.ruleSet);
      const requiredServiceModes = getRequiredServiceModes(offer.ruleSet);

      const isRedeemableAccordingToLimitRule = isRedeemableWithLimit(
        offer.ruleSet,
        unauthenticatedRedemptions[offer._id]
      );

      const shouldIncludeForPlatform = requiredPlatforms.length
        ? requiredPlatforms.includes(platform())
        : true;

      const shouldIncludeForServiceMode =
        !!serviceMode && requiredServiceModes.length
          ? requiredServiceModes.includes(serviceMode)
          : true;

      // Should only include Standard Offers UI pattern or no UI pattern specified
      const shouldIncludeForUIPattern = !offer.uiPattern || offer.uiPattern === UIPattern.STANDARD;

      // unauthenticated users cannot currently view coupons that
      // require an assignment from braze to use
      const shouldDisplayAssignmentOnly = !!(
        enableQueryOffersFromBackend ||
        user ||
        !requiresAssignmentWillApply(offer.ruleSet)
      );

      // enableQueryOffersFromBackend already validated this rule from BE
      const shouldDisplayRestaurantRestrictedOffer = enableQueryOffersFromBackend
        ? true
        : evaluateRestaurantGroupRestriction(offer.ruleSet, storeId);

      const shouldIncludeOption = !!offer?.option;

      let endDate: string = '';

      // eslint-disable-next-line no-unused-expressions
      offer.ruleSet?.forEach(rule => {
        if (rule.ruleSetType === RuleType.BetweenDates) {
          endDate = rule.endDate ?? '';
        }

        if (rule.ruleSetType === RuleType.And || rule.ruleSetType === RuleType.Or) {
          rule.ruleSet.forEach(r => {
            if (r.ruleSetType === RuleType.BetweenDates) {
              endDate = r.endDate ?? '';
            }
          });
        }
      });

      if (offer?.expiresOnDatetime) {
        endDate = new Date(offer.expiresOnDatetime).toString();
      }

      const isExpired = isAfter(Date.now(), new Date(endDate));

      const isValidOffer = [
        shouldIncludeForUIPattern,
        shouldIncludeForPlatform,
        shouldIncludeForServiceMode,
        shouldDisplayAssignmentOnly,
        shouldDisplayRestaurantRestrictedOffer,
        isRedeemableAccordingToLimitRule,
        shouldIncludeOption,
        !isExpired
      ].every(Boolean);

      return isValidOffer;
    },
    [enableQueryOffersFromBackend, serviceMode, storeId, unauthenticatedRedemptions, user]
  );

  const updateFilteredOffersList = useCallback(
    (queryData: IEvaluateUserOffersQuery | undefined, currentUserCognitoId: string | null) => {
      if ((!currentUserCognitoId && !enableQueryOffersFromBackend) || !queryData) {
        setOffersFeedback(undefined);
        setOffers(sortedOffers.filter(shouldDisplayOffer));
        return;
      }

      // Get all redeemable user offers in the same order as sortedOffers
      const { offersFeedback: offerFeedbackResult } = queryData.evaluateUserOffers;
      const normalOffers: IOffer[] = [];
      let filteredOffers: IOffer[];

      const sortedOffersFeedback: IOfferFeedbackEntryFragment[] = offerFeedbackResult.filter(
        (maybeFeedback): maybeFeedback is IOfferFeedbackEntryFragment => !!maybeFeedback
      );

      if (enableQueryOffersFromBackend) {
        filteredOffers = sortedOffersFeedback.reduce<IOffer[]>(
          (currFilteredOffers, offerFeedback) => {
            if (offerFeedback.offerDetails) {
              const offerData = JSON.parse(offerFeedback.offerDetails);
              let endDate: Date | null;

              // Parse variables Template
              const variables = templateKeyValuePairToObject({
                variables: Object.assign(offerFeedback.offerVariables || {})
              });

              // we use formation end date as default. If formation does not provide, we then use sanity end date.
              if (variables?.endDate) {
                // Need to retrieve the endDate from evaluate offers call
                endDate = new Date(variables.endDate);
              } else {
                // Get Sanity date
                const ruleSet = offerData.ruleSet || [];
                endDate = getExpirationDateFromRuleset(ruleSet);
              }

              // Turn all values into string
              // TODO what should the Datetimes look like?
              const variablesStringValues = mapValues(variables, val =>
                val ? val.toString() : ''
              );
              const updatedObj = parseSanityObject({
                doc: offerData,
                localeBlockKeys: LOCALE_BLOCK_KEYS,
                variables: variablesStringValues
              });

              // map vendor configs
              const vendorConfigs = {};

              // @todo move these logics to sanity package
              for (const key of Object.keys(offerData?.vendorConfigs || {})) {
                const allowedKeys = new Set([
                  'carrols',
                  'carrolsDelivery',
                  'ncr',
                  'ncrDelivery',
                  'productNumber',
                  'productNumberDelivery',
                  'sicom',
                  'sicomDelivery',
                  'qdi',
                  'qdiDelivery',
                  'rpos',
                  'tablet',
                  'tabletDelivery'
                ]);
                if (allowedKeys.has(key)) {
                  vendorConfigs[key] = {
                    pluType: offerData?.vendorConfigs?.[key]?.pluType || null,
                    parentSanityId: offerData?.vendorConfigs?.[key]?.parentSanityId || null,
                    pullUpLevels: offerData?.vendorConfigs?.[key]?.pullUpLevels || null,
                    constantPlu: offerData?.vendorConfigs?.[key]?.constantPlu || null,
                    quantityBasedPlu: offerData?.vendorConfigs?.[key]?.quantityBasedPlu
                      ? {
                          quantity: offerData?.vendorConfigs?.[key]?.quantityBasedPlu?.quantity,
                          plu: offerData?.vendorConfigs?.[key]?.quantityBasedPlu?.plu,
                          qualifier: offerData?.vendorConfigs?.[key]?.quantityBasedPlu?.qualifier
                        }
                      : null,
                    multiConstantPlus: offerData?.vendorConfigs?.[key]?.multiConstantPlus
                      ? {
                          quantity: offerData?.vendorConfigs?.[key]?.multiConstantPlus?.quantity,
                          plu: offerData?.vendorConfigs?.[key]?.multiConstantPlus?.plu,
                          qualifier: offerData?.vendorConfigs?.[key]?.multiConstantPlus?.qualifier
                        }
                      : null,
                    parentChildPlu: offerData?.vendorConfigs?.[key]?.parentChildPlu
                      ? {
                          plu: offerData?.vendorConfigs?.[key]?.parentChildPlu?.plu,
                          childPlu: offerData?.vendorConfigs?.[key]?.parentChildPlu?.childPlu
                        }
                      : null,
                    sizeBasedPlu: offerData?.vendorConfigs?.[key]?.sizeBasedPlu
                      ? {
                          comboPlu: offerData?.vendorConfigs?.[key]?.sizeBasedPlu?.comboPlu,
                          comboSize: offerData?.vendorConfigs?.[key]?.sizeBasedPlu?.comboSize
                        }
                      : null
                  };
                }
              }

              // Map to existing sanity offer shape.
              const newOffers = {
                ...updatedObj,
                expiresOnDatetime: endDate as Date,
                tokenId: offerFeedback?.tokenId,
                isRedeemable: offerFeedback.redemptionEligibility.isRedeemable,
                name: {
                  localeRaw: offerData?.name?.[language]
                },
                headerSuperText: {
                  locale: offerData?.headerSuperText?.[language]
                },
                description: {
                  localeRaw: offerData?.description?.[language]
                },
                moreInfo: {
                  localeRaw: offerData?.moreInfo?.[language]
                },
                howToRedeem: {
                  localeRaw: offerData?.howToRedeem?.[language]
                },
                offerCTA: {
                  actionText: {
                    locale: offerData?.offerCTA?.actionText?.[language]
                  },
                  route: offerData?.offerCTA?.route
                },
                localizedImage: {
                  locale: offerData?.localizedImage?.[language]
                },
                lockedOffersPanel: {
                  completedChallengeHeader: {
                    localeRaw: offerData?.lockedOffersPanel?.completedChallengeHeader?.[language]
                  },
                  completedChallengeDescription: {
                    localeRaw:
                      offerData?.lockedOffersPanel?.completedChallengeDescription?.[language]
                  }
                },
                promoCodePanel: {
                  campaignId: offerData?.promoCodePanel?.campaignId,
                  promoCodeDescription: {
                    localeRaw: offerData?.promoCodePanel?.promoCodeDescription?.[language]
                  },
                  promoCodeLabel: {
                    localeRaw: offerData?.promoCodePanel?.promoCodeLabel?.[language]
                  },
                  promoCodeLink: offerData?.promoCodePanel?.promoCodeLink
                },
                vendorConfigs
              };

              if (shouldDisplayOffer(newOffers)) {
                normalOffers.push(newOffers);
              }

              return currFilteredOffers.concat(newOffers);
            }
            return currFilteredOffers;
          },
          []
        );
      } else {
        const feedbacksById = offersFeedbacksById(sortedOffersFeedback);
        filteredOffers = sortedOffers.reduce<IOffer[]>((currFilteredOffers, offer) => {
          const offerFeedbacks = feedbacksById[offer._id];
          if (!offerFeedbacks) {
            return currFilteredOffers;
          }

          const validFeedbacks = offerFeedbacks.filter(
            ({ redemptionEligibility: { isRedeemable } }) => isRedeemable
          );
          const newOffers = validFeedbacks.map(oneFeedback => {
            let endDate: Date | null;

            // Parse variables Template
            const variables = templateKeyValuePairToObject({
              variables: Object.assign(oneFeedback.offerVariables || {})
            });

            // we use formation end date as default. If formation does not provide, we then use sanity end date.
            if (variables?.endDate) {
              // Need to retrieve the endDate from evaluate offers call
              endDate = new Date(variables.endDate);
            } else {
              // Get Sanity date
              const ruleSet = offer.ruleSet || [];
              endDate = getExpirationDateFromRuleset(ruleSet);
            }

            // Turn all values into string
            // TODO what should the Datetimes look like?
            const variablesStringValues = mapValues(variables, val => (val ? val.toString() : ''));
            const updatedObj = parseSanityObject({
              doc: offer,
              localeBlockKeys: LOCALE_BLOCK_KEYS,
              variables: variablesStringValues
            });

            return {
              ...updatedObj,
              expiresOnDatetime: endDate as Date,
              tokenId: oneFeedback?.tokenId,
              isRedeemable: oneFeedback.redemptionEligibility.isRedeemable
            };
          });

          // Remove added offers since sortedOffers can potentially have
          // offers with the same offer id.
          delete feedbacksById[offer._id];

          return currFilteredOffers.concat(newOffers);
        }, []);

        filteredOffers = filteredOffers.filter(shouldDisplayOffer);
      }

      const feedback = reduceOffersFeedback(sortedOffersFeedback);
      setOffersFeedback(feedback);
      setOffers(filteredOffers);
      setStandardOffers(normalOffers);
    },
    [enableQueryOffersFromBackend, language, shouldDisplayOffer, sortedOffers]
  );

  /**
   * track the loading state of ALL evaluateUserOffers requests in progress,
   * from the first useEvaluateUserOffersQuery to the last use of fetchMore
   **/
  useEffect(() => {
    // when the evaluateUserOffers query starts, set this to true as well
    if (loading) {
      setIsLoadingOrFetchingMore(true);
    }
  }, [loading]);

  /**
   * When all EvaluateUserOffer queries have run, call updateFilteredOffersList to update the offers list.
   * If updateFilteredOffersList runs before all offers have been evaluated,
   * the user may be kicked off of the redemption screen if their offer isn't in the list yet.
   **/
  useEffect(() => {
    // do not update filteredOffersList if more EvaluateUserOffers queries are still running
    if (!isLoadingOrFetchingMore) {
      updateFilteredOffersList(data, cognitoId);
    }
  }, [data, updateFilteredOffersList, cognitoId, isLoadingOrFetchingMore]);

  useEffect(() => {
    persistUnauthenticatedRedemptions(unauthenticatedRedemptions);
    setOffers(currentOffers => currentOffers.filter(shouldDisplayOffer));
  }, [unauthenticatedRedemptions]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    evaluateUserOffersLoading: enableQueryOffersFromBackend ? loading : isLoadingOrFetchingMore,
    offers,
    offersFeedback,
    queryRedeemableOffers,
    redeemUnauthenticatedOffer,
    refetch,
    standardOffers
  };
}

export default useOffersList;
